import React, { useState } from 'react';
import { X, Loader2, Mail } from 'lucide-react';
import { useGuestIdentity } from '../utils/useGuestIdentity';

/**
 * Tiny "Save & share your inspo list" identity capture.
 * Triggered only when user wants to share or create a list — not on every heart.
 */
const InspoSignInModal = ({ isOpen, onClose, onSuccess, headline = 'Save your inspo list' }) => {
  const { register } = useGuestIdentity();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !name.trim()) {
      setError('Please enter both your name and email.');
      return;
    }
    setBusy(true);
    try {
      const data = await register(email.trim(), name.trim());
      onSuccess && onSuccess(data);
      onClose && onClose();
    } catch (err) {
      setError(err.message || 'Could not save. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-5 bg-black/55 backdrop-blur-sm"
      data-testid="inspo-signin-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-sm bg-white text-black border border-black/5 shadow-2xl float-in" style={{ animation: 'floatIn 400ms cubic-bezier(0.22, 1, 0.36, 1) both' }}>
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <p className="text-[10px] tracking-[0.25em] uppercase text-black/40">Crystal · Inspo</p>
          <button type="button" onClick={onClose} aria-label="Close" className="text-black/40 hover:text-black tap-target" data-testid="inspo-signin-close">
            <X size={18} />
          </button>
        </div>
        <div className="px-6 pb-6">
          <h2 className="font-presti text-2xl leading-tight mb-2">{headline}</h2>
          <p className="text-black/55 text-sm mb-6 leading-relaxed">
            Drop your name + email so you can save, edit, and share your inspo list later.
            No password, no spam — promise.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label htmlFor="inspo-name" className="block text-[10px] tracking-[0.2em] uppercase text-black/40 mb-2">Your name</label>
              <input
                id="inspo-name"
                name="name"
                autoComplete="name"
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full px-0 py-2.5 bg-transparent border-b border-black/20 focus:border-black outline-none transition-colors"
                placeholder="Jane Doe"
                data-testid="inspo-signin-name"
                required
              />
            </div>
            <div>
              <label htmlFor="inspo-email" className="block text-[10px] tracking-[0.2em] uppercase text-black/40 mb-2">Email</label>
              <input
                id="inspo-email"
                type="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full px-0 py-2.5 bg-transparent border-b border-black/20 focus:border-black outline-none transition-colors"
                placeholder="you@email.com"
                data-testid="inspo-signin-email"
                required
              />
            </div>
            {error && <p className="text-red-500 text-xs" data-testid="inspo-signin-error">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 mt-2 px-6 py-3 bg-black text-white text-xs tracking-[0.2em] uppercase font-medium hover:bg-black/80 transition-colors disabled:bg-black/40"
              data-testid="inspo-signin-submit"
            >
              {busy ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : <><Mail size={14} /> Save & continue</>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InspoSignInModal;
